import React, { useEffect, useState, useCallback } from "react";
import api from "./axiosSetup";
import Select from "react-select";
import cfg from "../config";

const token = () => localStorage.getItem("authToken");
const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

// ─── Kleines Inline-Alert-Feedback ───────────────────────────────────────────
const Feedback = ({ msg, type = "success" }) =>
  msg ? <div className={`alert alert-${type} py-1 px-2 mt-2 mb-0 small`}>{msg}</div> : null;

// ─── Mitbenutzer-Sektion für eine Häs-Karte ──────────────────────────────────
const AccessSection = ({ haesID, allMembers, haesOwnerID, awardedToId, currentUserId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);

  const canManage = haesOwnerID === currentUserId || awardedToId === currentUserId;

  const flash = (msg, type = "success") => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/haes-access/${haesID}`, authHeader());
      setEntries(res.data);
    } catch {
      // ignorieren wenn kein Zugriff
    } finally {
      setLoading(false);
    }
  }, [haesID]);

  useEffect(() => {
    if (open) fetchEntries();
  }, [open, fetchEntries]);

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post("/api/haes-access", { haesID, memberID: selected.value }, authHeader());
      setSelected(null);
      setPickerOpen(false);
      flash("Mitbenutzer hinzugefügt");
      fetchEntries();
    } catch (err) {
      flash(err.response?.data?.message || "Fehler beim Hinzufügen", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await api.delete(`/api/haes-access/${id}`, authHeader());
      flash("Zugriff entfernt");
      fetchEntries();
    } catch (err) {
      flash(err.response?.data?.message || "Fehler beim Entfernen", "danger");
    }
  };

  // Mitglieder aus dem Picker ausschließen die bereits Zugriff haben oder Besitzer sind
  const existingIDs = new Set([haesOwnerID, ...entries.map((e) => e.memberID)]);
  const availableMembers = allMembers.filter((m) => !existingIDs.has(m.value));

  return (
    <div className="mt-3 border-top pt-3">
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="fw-semibold small text-secondary">
          Mitbenutzer{entries.length > 0 ? ` (${entries.length})` : ""}
        </span>
        <span className="text-secondary small">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="mt-2">
          {loading ? (
            <div className="text-muted small">Lädt…</div>
          ) : entries.length === 0 ? (
            <div className="text-muted small">Keine Mitbenutzer eingetragen.</div>
          ) : (
            <ul className="list-group list-group-flush mb-2">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="list-group-item px-0 py-1 d-flex align-items-center justify-content-between"
                >
                  <span className="small">{entry.memberName}</span>
                  {canManage && (
                    <button
                      className="btn btn-outline-danger btn-sm py-0 px-2 lh-1"
                      title="Zugriff entfernen"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(entry.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canManage && (
            <>
              {!pickerOpen ? (
                <button
                  className="btn btn-outline-success btn-sm w-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPickerOpen(true);
                  }}
                >
                  + Mitbenutzer hinzufügen
                </button>
              ) : (
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    options={availableMembers}
                    value={selected}
                    onChange={setSelected}
                    placeholder={`${cfg.memberTerm} suchen…`}
                    noOptionsMessage={() => `Nichts gefunden (${cfg.memberTerm})`}
                    menuPortalTarget={document.body}
                    styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                    isLoading={allMembers.length === 0}
                  />
                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-success btn-sm flex-fill"
                      onClick={handleAdd}
                      disabled={!selected || saving}
                    >
                      {saving ? "Speichert…" : "Speichern"}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setPickerOpen(false);
                        setSelected(null);
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <Feedback msg={feedback?.msg} type={feedback?.type} />
        </div>
      )}
    </div>
  );
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
const MyDataHaes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState(null);

  // Verleihen-State
  const [selectedLend, setSelectedLend] = useState({}); // { [haesID]: option }
  const [lendPickerOpen, setLendPickerOpen] = useState({}); // { [haesID]: bool }
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Bestätigungs-Modal
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [modalRow, setModalRow] = useState(null);

  useEffect(() => {
    fetchData();
    fetchUserId();
    fetchMembers();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/api/my-data-haeser", authHeader());
      setData(res.data);
    } catch {
      setError("Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserId = async () => {
    try {
      const res = await api.get("/api/my-data", authHeader());
      setUserId(res.data.ID);
    } catch {
      setError("Fehler beim Laden der Benutzerdaten.");
    }
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await api.get("/api/members-award-haes", authHeader());
      setMembers(
        res.data.map((m) => ({
          value: m.ID,
          label: `${m.firstName} ${m.lastName}`,
        }))
      );
    } catch {
      // nicht kritisch
    } finally {
      setLoadingMembers(false);
    }
  };

  // ── Verleihen ──
  const handleLendClick = (row) => {
    setModalRow(row);
    setModalAction("verleihen");
    setLendPickerOpen((p) => ({ ...p, [row.ID]: true }));
  };

  const confirmLend = (row) => {
    if (!selectedLend[row.ID]) return;
    setModalRow(row);
    setModalAction("verleihen");
    setShowModal(true);
  };

  const handleReturnClick = (row, action) => {
    setModalRow(row);
    setModalAction(action);
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (!modalRow) return;
    try {
      const lendMember = selectedLend[modalRow.ID];
      if (modalAction === "verleihen") {
        await api.post(
          "/api/my-data-award-haes",
          {
            haesId: modalRow.ID,
            ownerId: modalRow.memberID,
            awardedTo: lendMember.value,
            ownerName: modalRow.ownerName,
            number: modalRow.number,
            awardedName: lendMember.label,
          },
          authHeader()
        );
      } else if (modalAction === "zurueckgeben") {
        await api.post(
          "/api/my-data-return-haes",
          {
            haesId: modalRow.ID,
            ownerId: modalRow.memberID,
            awardedTo: modalRow.awardedTo,
            ownerName: modalRow.ownerName,
            number: modalRow.number,
            awardedName: modalRow.awardedName,
          },
          authHeader()
        );
      } else if (modalAction === "removeAward") {
        await api.post(
          "/api/my-data-remove-award-haes",
          {
            haesId: modalRow.ID,
            ownerId: modalRow.memberID,
            awardedTo: modalRow.awardedTo,
            ownerName: modalRow.ownerName,
            number: modalRow.number,
            awardedName: modalRow.awardedName,
          },
          authHeader()
        );
      }
      setShowModal(false);
      setSelectedLend((p) => ({ ...p, [modalRow.ID]: null }));
      setLendPickerOpen((p) => ({ ...p, [modalRow.ID]: false }));
      setModalRow(null);
      fetchData();
    } catch {
      setError("Fehler bei der Aktion.");
      setShowModal(false);
    }
  };

  // ── Anzuzeigende Felder ──
  // Die Schnittstelle liefert neutrale Feldnamen; Reihenfolge und Beschriftung
  // stehen hier, damit sie den Begriffen aus der Konfiguration folgen können.
  // Interne Felder (memberID, awardedTo, ID, userRole) fehlen bewusst.
  const FELDER = [
    { key: "number",         label: "Nummer" },
    { key: "typ",            label: "Typ" },
    { key: "ownerName",      label: "Besitzer" },
    { key: "isKidsHaes",     label: `Kinder-${cfg.costumeTerm}` },
    { key: "isRentHaes",     label: "Leihgabe" },
    { key: "accessories",    label: "Zubehör" },
    { key: "hasTreadmills",  label: "Laufbendel" },
    { key: "awardedName",    label: "Verliehen an" },
    { key: "lastTreadmills", label: "Letzter Laufbendel" },
  ];

  // Wahrheitswerte kommen jetzt als echte Booleans — React würde sie sonst
  // stillschweigend gar nicht ausgeben.
  const zeigeWert = (wert) => {
    if (typeof wert === "boolean") return wert ? "Ja" : "Nein";
    return (wert ?? "") === "" ? "—" : wert;
  };

  if (loading) return <div className="p-3">Lädt…</div>;
  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  const ownHaeser    = data.filter((r) => r.userRole !== "shared");
  const sharedHaeser = data.filter((r) => r.userRole === "shared");

  const renderCard = (row) => {
    const canManage = row.memberID === userId;
    const isLendee  = row.awardedTo === userId;
    const isShared  = row.userRole === "shared";

    return (
      <div key={row.ID} className="card shadow-sm" style={{ minWidth: "280px", maxWidth: "320px", flex: "1 1 280px" }}>
        <div className="card-body">
          {isShared && (
            <span className="badge bg-secondary mb-2">Mitbenutzer</span>
          )}
          {/* Häs-Daten */}
          {FELDER.map(({ key, label }) => (
            <p key={key} className="mb-1">
              <strong>{label}:</strong> {zeigeWert(row[key])}
            </p>
          ))}

          {/* Verleihen-Aktionen (nur für Besitzer / Ausgeliehene) */}
          {!isShared && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {canManage && !row.awardedTo && (
                <button className="btn btn-primary btn-sm" onClick={() => handleLendClick(row)}>
                  Verleihen
                </button>
              )}
              {canManage && row.awardedTo && (
                <button className="btn btn-danger btn-sm" onClick={() => handleReturnClick(row, "removeAward")}>
                  Verleihen zurückziehen
                </button>
              )}
              {isLendee && (
                <button className="btn btn-warning btn-sm" onClick={() => handleReturnClick(row, "zurueckgeben")}>
                  Rückgabe an Besitzer
                </button>
              )}
            </div>
          )}

          {/* Verleihen-Picker (inline) */}
          {canManage && lendPickerOpen[row.ID] && !showModal && (
            <div className="mt-3">
              {loadingMembers ? (
                <div className="text-muted small">Lädt {cfg.memberTermPlural}…</div>
              ) : (
                <>
                  <Select
                    options={members}
                    value={selectedLend[row.ID] || null}
                    onChange={(m) => setSelectedLend((p) => ({ ...p, [row.ID]: m }))}
                    placeholder={`${cfg.memberTerm} auswählen…`}
                    menuPortalTarget={document.body}
                    styles={{ menu: (b) => ({ ...b, zIndex: 9999 }) }}
                  />
                  <div className="d-flex gap-2 mt-2">
                    <button className="btn btn-primary btn-sm" onClick={() => confirmLend(row)} disabled={!selectedLend[row.ID]}>
                      Speichern
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setLendPickerOpen((p) => ({ ...p, [row.ID]: false }))}>
                      Abbrechen
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mitbenutzer-Sektion (nur für Besitzer / Ausgeliehene sichtbar) */}
          {!isShared && userId && (
            <AccessSection
              haesID={row.ID}
              haesOwnerID={row.memberID}
              awardedToId={row.awardedTo}
              currentUserId={userId}
              allMembers={members}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <h2>Meine {cfg.costumeTermPlural}</h2>

      {ownHaeser.length === 0 && sharedHaeser.length === 0 && (
        <div className="alert alert-warning">Keine Daten verfügbar.</div>
      )}

      {/* ── Eigene / ausgeliehene Häser ── */}
      {ownHaeser.length > 0 && (
        <div className="d-flex flex-wrap gap-3 mb-4">
          {ownHaeser.map(renderCard)}
        </div>
      )}

      {/* ── Häser als Mitbenutzer ── */}
      {sharedHaeser.length > 0 && (
        <>
          <h4 className="mt-2 mb-3 text-secondary">{cfg.costumeTermPlural}, auf die du Mitbenutzer-Zugriff hast</h4>
          <div className="d-flex flex-wrap gap-3">
            {sharedHaeser.map(renderCard)}
          </div>
        </>
      )}

      {/* ── Bestätigungs-Modal ── */}
      {showModal && modalRow && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="bg-white p-4 rounded shadow"
            style={{ width: "100%", maxWidth: "400px", color: "black" }}
          >
            <h5 className="mb-3">Bestätigung</h5>
            <p>
              Möchtest du wirklich{" "}
              {modalAction === "verleihen"
                ? `${cfg.costumeTerm} an ${selectedLend[modalRow.ID]?.label || cfg.memberTerm} verleihen?`
                : modalAction === "removeAward"
                ? "das Verleihen zurückziehen?"
                : `${cfg.costumeTerm} an den Besitzer zurückgeben?`}
            </p>
            <p className="text-danger fw-bold mt-2 mb-0" style={{ fontSize: "0.9rem" }}>
              Wenn du das bestätigst, werden auch alle Buchungen für dieses {cfg.costumeTerm} entfernt!
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={handleConfirm}>
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDataHaes;
